import React from "react"
import type { MouseEvent } from "react"
import type { FieldValues } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { FormTextField } from "@/nzds/forms"

import { Visibility, VisibilityOff } from "@mui/icons-material"
import { IconButton, InputAdornment } from "@mui/material"

import type { PasswordTextFieldProps } from "./password-text-field.types"

export const PasswordTextField = <TSchema extends FieldValues>({
  autoComplete = "new-password",
  id,
  isPasswordVisible,
  label,
  onPasswordVisibleClick,
  rightAdornment,
  ...props
}: PasswordTextFieldProps<TSchema>) => {
  const { t } = useTranslation()

  const handlePasswordVisibleClick = React.useCallback(
    (event: MouseEvent): void => {
      onPasswordVisibleClick(event)
    },
    [onPasswordVisibleClick]
  )

  const handlePasswordVisibleMouseDown = React.useCallback(
    (event: MouseEvent): void => {
      event.preventDefault()
    },
    []
  )

  return (
    <FormTextField<TSchema>
      fullWidth
      id={id}
      slotProps={{
        htmlInput: {
          autoComplete,
          type: isPasswordVisible ? "text" : "password",
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              {rightAdornment}
              <IconButton
                aria-label={t("button.togglePasswordVisibility")}
                onClick={handlePasswordVisibleClick}
                onMouseDown={handlePasswordVisibleMouseDown}
                edge="end"
              >
                {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
      label={label}
      name={id}
      preserveHelperTextSpacing
      {...props}
    />
  )
}
